import React, { memo, useEffect, useState, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { validators, strings, objects } from 'investira.sdk';
import { Stack, CircularProgress, TextField, InputAdornment, Icon, Typography, Dialog, Divider, IconButton, Skeleton, DialogContent, useMediaQuery } from 'investira.react.components';
import services from '../../../services';
import utils from '../../../utils';
import withResponseHandling from '../../../hoc/withResponseHandling';
import { acSearchAtivosChanged } from '../../../store/actions';
import SearchAtivosAddItem from './SearchAtivosAddItem';

/**
 * Renderiza o componente SearchAtivosAdd.
 *
 * @param {Object} props - Propriedades do componente.
 * @param {*} props.pageIsLoading - Propriedade `pageIsLoading` do componente.
 * @param {*} props.clearOnClose - Propriedade `clearOnClose` do componente.
 * @param {*} props.responseErrorHandling - Propriedade `responseErrorHandling` do componente.
 * @param {*} props.onSearch - Propriedade `onSearch` do componente.
 * @param {*} props.onData - Propriedade `onData` do componente.
 *
 * @description
 * Este componente realiza as seguintes operacoes:
 * 1. Recebe as propriedades necessarias para compor a interface
 * 2. Organiza os estados e callbacks usados durante a renderizacao
 * 3. Renderiza a experiencia visual do organismo
 *
 * @returns {JSX.Element|null} Elemento renderizado pelo componente.
 */

const SearchAtivosAdd = memo(props => {
  const PAGE_SIZE = 25;
  const search = useSelector(store => store.searchAtivos);
  const navigate = useNavigate();
  const storeDispatch = useDispatch();
  const [isLoading, setRequesting] = useState(false);
  const [pesquisa, setPesquisa] = useState('');
  const [data, setData] = useState({});
  const [, setPages] = useState({});
  const [message, setMessage] = useState(null);
  const [open, setOpen] = useState(null);
  const inputRef = useRef();
  const placeholderRef = useRef();
  const xIsConferenciaSearch = props?.searchType === 'conferencia';
  const handleOpen = pEvent => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    placeholderRef.current.blur();
    inputRef.current.blur();
    handleClearPesquisa();
  };
  const handleChange = pEvent => {
    const xValue = pEvent.currentTarget.value;
    setPesquisa(pEvent.currentTarget.value);
    handleOpen(pEvent);
    if (validators.isEmpty(xValue)) {
      handleClose();
      return;
    }
  };
  const openDrawerOnFocus = pEvent => {
    placeholderRef.current.blur();
    handleOpen(pEvent);
  };
  const handleClearPesquisa = (pLocation = {}) => {
    setPesquisa('');
    setData({});
    setPages({});
    if (!validators.isEmpty(pLocation?.search)) {
      const xSearch = strings.querystringToObject(pLocation.search);
      if (!validators.isEmpty(xSearch.pesquisa)) {
        delete xSearch.pesquisa;
        const xQuery = objects.objectToQueryString(xSearch);
        navigate(`/ativos${xQuery}`);
      }
    }
  };
  const handleAdd = (pEvent, pAtivo, pClearOnClose) => {
    pEvent.preventDefault();
    pEvent.stopPropagation();
    props.onClick(pAtivo.ifeed_ativo_id);

    // const xPath = PATHS[pMercado.toLowerCase()].path;

    handleClose();
    pClearOnClose && handleClearPesquisa();
  };
  function verifySpecialChar(pStr) {
    const regex = /[./-]/;
    return regex.test(pStr);
  }
  function cleanSpecialChar(pStr) {
    const regex = /[./-]/g;
    if (verifySpecialChar(pStr)) {
      return pStr.replace(regex, '');
    }
    return pStr;
  }

  // function isNumeric(pStr) {
  //   if (typeof pStr !== "string") {
  //     return false;
  //   }

  //   return /^[0-9]+$/.test(pStr);
  // }

  const request = useMemo(() => {
    const xFn = xIsConferenciaSearch ? services.conferenciasCustodiantes.ativos : services.ativosbase.list;
    return utils.helpers.debounce((pParams, pRes, pErr, pFin) => xFn(pParams, pRes, pErr, pFin), 1000);
  }, []);
  function removeDoubleSpaces(pStr) {
    return pStr.replace(/\s{2,}/g, ' ').trim();
  }
  function removeEmptyItems(pArr) {
    return pArr.filter(item => item !== '');
  }
  function formatSearch(pValue) {
    let xValue = removeDoubleSpaces(pValue);
    xValue = cleanSpecialChar(pValue);
    let xStrings = xValue.split(' ');
    xStrings = removeEmptyItems(xStrings);
    xStrings = xStrings.map(xString => {
      return `+${xString}*`.trim();
    });
    const xSearchFormated = xStrings.join('');
    return xSearchFormated;
  }
  useEffect(() => {
    if (open) {
      placeholderRef.current.blur();
      inputRef.current.focus();
      setData(search.result);
      setPages(search.pages);
    }
  }, [open, inputRef.current, placeholderRef.current]);
  useEffect(() => {
    if (pesquisa && pesquisa.length > 1 && !props.pageIsLoading) {
      setRequesting(true);
      setData({});
      setMessage(null);
      const xPesquisa = formatSearch(pesquisa);
      const xParams = {
        pesquisa: xPesquisa,
        size: PAGE_SIZE,
        cancelado: 'null',
        fracionario: 0,
        prazo: 'VST'
      };
      if (!xIsConferenciaSearch) {
        xParams.sort = 'relevancia DESC, __mr__ DESC, mercado ASC';
      }
      if (!validators.isEmpty(props.mercado)) {
        xParams.mercado = props.mercado;
      }
      if (!validators.isEmpty(props.visao)) {
        xParams.visao = props.visao;
      }
      if (!validators.isEmpty(props.visao)) {
        xParams.possui_gestor = props.possuiGestor;
      }
      request({
        ...xParams
      }, rRes => {
        setData(rRes.data);
        setPages(rRes.pages);
        storeDispatch(acSearchAtivosChanged({
          pesquisa: pesquisa,
          result: rRes.data,
          pages: rRes.pages
        }));
        if (validators.isEmpty(rRes.data)) {
          setMessage('Nenhum dado encontrado');
        }
      }, rErr => {
        setMessage('Erro na busca por empresa');
      }, () => {
        setRequesting(false);
      });
    }
  }, [pesquisa, request]);
  const isUpMd = useMediaQuery(theme => theme.breakpoints.up('md'));
  const xDialogProps = isUpMd ? {
    fullScreen: false,
    PaperProps: {
      sx: {
        pt: 3,
        pb: 2,
        px: 2
      }
    },
    maxWidth: 'lg'
  } : {
    fullScreen: true,
    PaperProps: {
      sx: {
        margin: 0,
        backgroundColor: 'background.dark'
      }
    }
  };
  return <>
      <Stack id="search-area" sx={{
      position: 'relative',
      width: '100%',
      zIndex: '999'
    }}>
        {props.pageIsLoading ? <Skeleton variant="rounded" width="100%" height={56} /> : <TextField id="placeholder-busca-empresa" inputRef={placeholderRef} value={pesquisa} onFocus={openDrawerOnFocus} variant="outlined" placeholder={props?.placeholder || 'Pesquise por ativos'} fullWidth InputProps={{
        startAdornment: <InputAdornment position="start">
                  <Icon iconName="find" size={21} color={'primary'} />
                </InputAdornment>
      }} disabled={props.pageIsLoading} />}

        <Dialog {...xDialogProps} open={open} onClose={handleClose} keepMounted fullWidth>
          <DialogContent>
            <Stack direction="row" alignItems="center" spacing={2} mb={1}>
              <Stack>
                <IconButton onClick={handleClose} color={'primary'}>
                  <Icon iconName="arrow_left" size={21} />
                </IconButton>
              </Stack>
              <Stack flexGrow={1}>
                <TextField id="busca-empresa" inputRef={inputRef} value={pesquisa} onChange={handleChange} sx={{
                border: 'none'
              }} variant="outlined" size={isUpMd ? 'medium' : 'small'} placeholder={'Pesquise por fundos'} fullWidth disabled={props.pageIsLoading} />
              </Stack>
            </Stack>
            <Divider />
            <Stack>
              {!isLoading && !validators.isEmpty(data) && <>
                  <Stack id="busca-empresa-list" sx={{
                px: 0
              }} component="ul">
                    {Object.values(data).map(xAtivo => {
                  return <SearchAtivosAddItem key={crypto.randomUUID()} ativo={xAtivo} onClick={e => handleAdd(e, xAtivo, props.clearOnClose)}
                  // onClick={(e) =>
                  //   handleGoTo(
                  //     e,
                  //     xAtivo.ifeed_ativo_id,
                  //     xAtivo.mercado,
                  //     props.clearOnClose
                  //   )
                  // }
                  />;
                })}
                  </Stack>
                  {/* <Stack
                    sx={{
                      px: 2,
                      py: 2,
                      alignItems: "center",
                    }}
                   >
                    <Button
                      variant="contained"
                      onClick={() => handleSearch(pesquisa)}
                      sx={{ maxWidth: "500px", px: 4 }}
                    >
                      Visualizar Todos ({TOTAL_ITEMS})
                    </Button>
                   </Stack> */}
                </>}

              {isLoading && <Stack justifyContent="center" alignItems="center" flexGrow={1} sx={{
              py: 2
            }}>
                  <CircularProgress size={24} />
                </Stack>}

              {!isLoading && validators.isEmpty(data) && <Stack justifyContent="center" alignItems="center" flexGrow={1} sx={{
              py: 2
            }}>
                  <Typography variant="body2" color="textPrimary">
                    {message}
                  </Typography>
                </Stack>}
            </Stack>
          </DialogContent>
        </Dialog>
      </Stack>
    </>;
});
export default withResponseHandling(SearchAtivosAdd);
